import React, { useState, useEffect, useCallback } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { styles } from "../styles";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { Campaign, NewCampaign } from "model/campaign";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";
import { DynamicContentType } from "type/dynamic-content";
import { Select } from "component/shared/select";
import { createCampaign, updateCampaign } from "redux/slice/campaigns";
import { getMarketsOptimized } from "redux/selector";
import * as yup from "yup";
import { validate } from "shared/yup";
import { EmailMarketingType } from "type/email-marketing-type";
import * as env from "shared/env";


interface Props extends
	OwnProps,
	WithMediaQuery,
	WithStyles<typeof styles>
{
}

interface OwnProps {
	campaign?: Campaign;
	onSave?: () => void;
}

const Component: React.FC<Props> = ({ campaign, onSave, classes = {} }) => {
	const [submitted, setSubmitted] = useState(false);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [dynamicContentType, setDynamicContentType] = useState<DynamicContentType | undefined>(DynamicContentType.LEAD_PROFILE);
	const [emailBranding, setEmailBranding] = useState(false);
	const [marketId, setMarketId] = useState<number | undefined>(undefined);

	const markets = useSelector(getMarketsOptimized);
	const dispatch = useAppThunkDispatch();

	const validationSchema = {
		name: yup
		.string()
		.required("Name is required.")
	};

	useEffect(() => {
		if (campaign) {
			setName(campaign.name);
			setDescription(campaign.description ? campaign.description : "");
			setEmailBranding(campaign.emailBrandingYn ? campaign.emailBrandingYn : false);
			setDynamicContentType(campaign.campaignType && campaign.campaignType);
			setMarketId(campaign.marketId);
		}
	}, [campaign]);

	const handleTextField = useCallback((id: string, value: string) => {
		if (id === "name") setName(value);
		if (id === "description") setDescription(value);
	}, []);

	const handleCheckField = useCallback((id: string, value: boolean) => {
		if (id === "emailBranding") setEmailBranding(value);
	}, []);

	const handleMarketField = useCallback((value: number) => {
		setMarketId(value);
	}, []);

	const handleSave = useCallback(() => {
		const state = { name, description, dynamicContentType, emailBranding, marketId };
		const errors = validate(validationSchema, state);
		if (!errors) {
			if (campaign && window.confirm("Save Settings?")) {
				const updatedCampaign: Campaign = {
					...campaign,
					name: name,
					description: description,
					emailBrandingYn: emailBranding,
					campaignType: dynamicContentType,
					marketId: marketId,
					emailMarketingType: EmailMarketingType.DRIP,
				}
				dispatch(updateCampaign({campaign: updatedCampaign}));
			} else {
				const newCampaign: NewCampaign = {
					name: name,
					description: description,
					emailBrandingYn: emailBranding,
					campaignType: dynamicContentType,
					marketId: marketId,
					emailMarketingType: EmailMarketingType.DRIP,
				}
				dispatch(createCampaign({campaign: newCampaign}));
			}
			if (onSave) {
				onSave();
			}
		}
	}, [name, description, dynamicContentType, emailBranding, marketId, campaign, dispatch, onSave, validationSchema]);

	const state = { name, description, dynamicContentType, emailBranding, marketId };
	const errors = validate(validationSchema, state);

	return (
		<>
			<Mui.Grid container direction="column" spacing={1} style={{marginTop: campaign && campaign.id && 15}}>
				<Mui.Grid item lg={6}>
					<Mui.TextField
						error={submitted && !!errors && !!errors.name}
						id="name"
						variant="outlined"
						margin="dense"
						value={name}
						label="Name"
						fullWidth 
						onChange={(event) => handleTextField(event.target.id, event.target.value)}
					/>
				</Mui.Grid>	
				<Mui.Grid item lg={6}>
					<Mui.TextField
						id="description"
						variant="outlined"
						margin="dense"
						value={description}
						label="Description"
						multiline
						minRows={4}
						fullWidth 
						onChange={(event) => handleTextField(event.target.id, event.target.value)}
					/>
				</Mui.Grid>	
				<Mui.Grid item lg={6}>
					<Mui.Grid container direction="row" alignItems="center" wrap="nowrap">
						<Mui.Grid item className={classes.dynamicContentContainer}>
							<Select
								fullWidth
								variant="outlined"
								margin="dense"
								placeholder="Select"
								label="Smart Content Based On"
								value={(dynamicContentType && dynamicContentType.id) || ""}
								options={DynamicContentType.values()}
								valueExtractor={type => type.id}
								labelExtractor={type => type.label}
								optionRenderer={type => <Mui.ListItemText primary={type.label} />}
								onChange={value => setDynamicContentType(DynamicContentType.getById(value))}
							/>
						</Mui.Grid>
						<Mui.Grid item>
							<div className={classes.info} >
								<Mui.Tooltip title="Select the source for dynamically generated content such as new listings and statistics.">
									<span>
										<FeatherIcon>
											<Icons.Info />
										</FeatherIcon>
									</span>
								</Mui.Tooltip>
							</div>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
					
				{dynamicContentType === DynamicContentType.MARKET && 
					<Mui.Grid item lg={6}>
						<Select
							variant="outlined"
							margin="dense"
							placeholder="Select a Market"
							fullWidth
							value={marketId}
							options={markets}
							valueExtractor={type => type.id}
							labelExtractor={type => type.name}
							optionRenderer={type => <Mui.ListItemText primary={type.name} />}
							onChange={value => handleMarketField(value)}
						/>
					</Mui.Grid>	
				}
				<Mui.Grid item lg={6}>
					<Mui.FormControlLabel
						control={
							<Mui.Checkbox
								id="emailBranding"
								checked={emailBranding}
								onChange={(event) => handleCheckField(event.target.id, event.target.checked)}
							/>
						}
						label="Email Branding"	
					/>
				</Mui.Grid>	
				<Mui.Grid item lg={6}>
					<Mui.Button
						variant="contained"
						color={"primary"}
						onClick={() => {
							setSubmitted(true);
							handleSave();
						}}
					>
						{campaign && campaign.id ? 
							(<>
								Save
							</>) :
							(<>
								Add
							</>)
						}
					</Mui.Button>
				</Mui.Grid>
			</Mui.Grid>
		</>
	);
};

export const EditCampaign = withMediaQuery("(max-width:600px)")(withStyles(Component, styles));